import { defineComponent as _defineComponent } from 'vue'
import { AppDirection } from '@/enums/app-direction';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { useMounted, usePointerSwipe } from '@vueuse/core';
import type { SwiperInstance } from 'tiny-swiper';

import { computed, onActivated, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { observableOptions } from '@/helpers/composables/useOfferImpressionTracking';
import { NATIVE_AD_RENDERED_KEY } from '@/features/native-ads/helpers/native-ad-config';
import { storeToRefs } from 'pinia';
import { useMetaStore } from '@/stores/pinia/useMetaStore';
import { useExperiment } from '@/libraries/experiment';


export default /*#__PURE__*/_defineComponent({
  __name: 'FreeTrialSwiperOSContainer',
  props: {
    itemCount: null,
    delay: null,
    initialSlide: { default: 0 },
    persistedSlideIndex: null,
    fullLine: { type: Boolean },
    isPPSwiper: { type: Boolean },
    isPromoted: { type: Boolean },
    hideControls: { type: Boolean, default: false },
    arrowStatic: { type: Boolean, default: true },
    persistedAutoSwipe: null,
    isNativeSQTVActive: { type: Boolean, default: false }
  },
  emits: ['slideChange'],
  setup(__props: any, { expose, emit: emits }) {

const props = __props as { itemCount: number, delay: number, initialSlide: number, persistedSlideIndex: number, fullLine?: boolean, isPPSwiper?: boolean, isPromoted?: boolean, hideControls: boolean, arrowStatic: boolean, persistedAutoSwipe?: number, isNativeSQTVActive: boolean };

let swiper: SwiperInstance | null = null;
const currentSlideIndex = ref(0);
const itemContainer = ref<HTMLElement | null>(null);
const isMounted = useMounted();



const { appDirection } = storeToRefs(useMetaStore());
const { activeVariant: underperformingVariant } = useExperiment('EXP_UNDERPERFORMING_URLS_4');

/**
 * Only auto swipe after swiper in in the viewport
 */
const autoSwipeOnVisiblity = computed(() => props.isPPSwiper && props.initialSlide > 0);

if (!IS_TOUCH) {
	const { isSwiping } = usePointerSwipe(itemContainer);
	// Ignore click events *while* swiping on desktop
	watch(isSwiping, isCurrentlySwiping => {
		if (!isCurrentlySwiping) return;

		itemContainer.value?.addEventListener(
			'click',
			e => {
				e.preventDefault();
				e.stopPropagation();
			},
			{ once: true, capture: true }
		);
	});
}

const isRTL = computed(() => appDirection.value === AppDirection.RTL);

const hasAutoSwiped = ref(false);
const hasBeenObserved = ref(false);
const nativeAdHasRendered = ref(false);
const autoSwipeIntervalId = ref<ReturnType<typeof setInterval> | null>(null);

provide(NATIVE_AD_RENDERED_KEY, onNativeAdRendered);

onActivated(initSwiper);

const observeVisibilityOptions = computed(() => ({
	...observableOptions,
	callback: (isVisible: boolean) => onVisibilityChanged(isVisible),
}));

onMounted(() => {
	initSwiper();
	window.addEventListener('resize', handleResize);

	// UNDERPERFORMING EXP 4
	if (underperformingVariant.value) startAutoSwipe();
	// UNDERPERFORMING EXP 4
});

onBeforeUnmount(() => {
	window.removeEventListener('resize', handleResize);
	clearAutoSwipeInterval();
});

function clearAutoSwipeInterval() {
	if (autoSwipeIntervalId.value) {
		clearInterval(autoSwipeIntervalId.value);
		autoSwipeIntervalId.value = null;
	}
}

function startAutoSwipe() {
	if (hasAutoSwiped.value) return;
	hasAutoSwiped.value = true;

	// Swipe forwards after delay
	setTimeout(() => {
		swiper?.slideTo(props.initialSlide);
		changeSlideIndex({ toIndex: props.initialSlide, direction: 'right', nonInteraction: true });
	}, props.delay);

	// Continuous auto swipe - currently only required for Native SQTV
	if (props.persistedAutoSwipe) {
		autoSwipeIntervalId.value = setInterval(() => {
			const toIndex = (currentSlideIndex.value + 1) % props.itemCount;
			swiper?.slideTo(toIndex);
			changeSlideIndex({ toIndex, direction: 'right', nonInteraction: true });
		}, props.persistedAutoSwipe);
	}
}

function onVisibilityChanged(isVisible: boolean) {
	if (isVisible && !hasBeenObserved.value) {
		hasBeenObserved.value = true;

		if (!props.isNativeSQTVActive) {
			startAutoSwipe();
		} else if (nativeAdHasRendered.value) {
			startAutoSwipe();
		}
	}
}

function next(allowRTL: boolean = false) {
	if (isRTL.value && !allowRTL) {
		prev(true);
		return;
	}

	const toIndex = currentSlideIndex.value === props.itemCount - 1 ? 0 : currentSlideIndex.value + 1;
	swiper?.slideTo(toIndex);
	changeSlideIndex({ toIndex, direction: 'right' });
}

function prev(allowRTL: boolean = false) {
	if (isRTL.value && !allowRTL) {
		next(true);
		return;
	}

	const toIndex = currentSlideIndex.value === 0 ? props.itemCount - 1 : currentSlideIndex.value - 1;
	swiper?.slideTo(toIndex);
	changeSlideIndex({ toIndex, direction: 'left' });
}

function changeSlideIndex({
	toIndex,
	direction,
	nonInteraction = false,
}: {
	toIndex: number;
	direction: 'left' | 'right';
	nonInteraction?: boolean;
}): void {
	const prevIndex = currentSlideIndex.value;
	currentSlideIndex.value = toIndex;

	if (prevIndex !== null && toIndex !== prevIndex && isMounted.value) {
		emits('slideChange', {
			direction: direction,
			position: toIndex,
			nonInteraction: nonInteraction,
		});
	}
}

function initSwiper(persistedSlideIndex: number = 0) {
	try {
		// swiper has a top level global variable calling `requestAnimationFrame` so the import needs to be client only
		// Because of a watcher in the tree, this function should not be async
		import('tiny-swiper').then(({ default: Swiper }) => {
			if (itemContainer.value && process.client) {
				if (swiper == null) {
					swiper = new Swiper(itemContainer.value, {
						// initialSlide: persistedSlideIndex,
						loop: false,
					});
				}

				if (!autoSwipeOnVisiblity.value) {
					if (persistedSlideIndex === 0 && props.initialSlide > 0) {
						setTimeout(() => {
							swiper?.slideTo(props.initialSlide);
							changeSlideIndex({ toIndex: props.initialSlide, direction: 'right', nonInteraction: true });
						}, props.delay);
					} else {
						currentSlideIndex.value = persistedSlideIndex;
					}
				}
				swiper.on('before-slide', (current: number, state: any, next: number) => {
					// on element swipe state.isTouching === true
					if (state?.isTouching) {
						const direction = current > next ? 'left' : 'right';
						changeSlideIndex({
							toIndex: next,
							direction,
							nonInteraction: false,
						});
					}
				});
			}
		});
	} catch (error: any) {
		captureMessageForSentry('[Initialize Tiny Swiper]', { error, where: '[TinySwiper]: initSwiper' }, 'error');
	}
}

function handleResize() {
	if (swiper?.update) {
		swiper.update();
	}
}

/* Native SQTV only
 * Provide callback for NativeAdSQTV to call when ad renders
 * We want to delay autoswipe until we confirm the ad has rendered
 */

function onNativeAdRendered() {
	if (!props.isNativeSQTVActive) return;

	nativeAdHasRendered.value = true;

	// If we've already observed the component, start autoswipe immediately
	if (hasBeenObserved.value && !hasAutoSwiped.value) {
		startAutoSwipe();
	}
}

expose({
	next,
	prev,
	currentSlideIndex,
});

return { __sfc: true,swiper, currentSlideIndex, itemContainer, isMounted, props, emits, appDirection, underperformingVariant, autoSwipeOnVisiblity, isRTL, hasAutoSwiped, hasBeenObserved, nativeAdHasRendered, autoSwipeIntervalId, observeVisibilityOptions, clearAutoSwipeInterval, startAutoSwipe, onVisibilityChanged, next, prev, changeSlideIndex, initSwiper, handleResize, onNativeAdRendered, faChevronLeft, faChevronRight }
}

})